import { IonIcon } from '@ionic/react';
import { hourglass } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { MARKET_GRACE_PERIOD_MINUTES } from '../../../../appConfig';
import { useMarketHours } from '../../../../hooks/useMarketHours';
import { formatCountdown, getMadridMinutesSinceMidnight, madridWallClockToEpochMs } from '../../../../utils/time';

interface TimeInfo {
  label: string;
  countdown: string | null;
  color: 'red' | 'orange' | 'yellow' | 'green' | 'gray';
  showTimer: boolean;
}

export const MarketTimeCounter = () => {
  const { closeTime, openTime } = useMarketHours();
  const [timeInfo, setTimeInfo] = useState<TimeInfo>(() => getTimeInfo(closeTime, openTime));

  useEffect(() => {
    // Update every 100ms to show tenths of seconds when < 1 minute
    const interval = setInterval(() => {
      setTimeInfo(getTimeInfo(closeTime, openTime));
    }, 100);

    return () => clearInterval(interval);
  }, [closeTime, openTime]);

  const colorClasses = {
    red: 'text-red-600 bg-red-50',
    orange: 'text-orange-600 bg-orange-50',
    yellow: 'text-yellow-600 bg-yellow-50',
    green: 'text-green-600 bg-green-50',
    gray: 'text-gray-600 bg-gray-50',
  };

  return (
    <div className={`w-full mb-4 p-3 rounded-lg ${colorClasses[timeInfo.color]}`}>
      <p className="text-sm font-semibold text-center">{timeInfo.label}</p>
      <div className="flex items-center justify-center gap-2 mt-1">
        {timeInfo.showTimer && <IonIcon icon={hourglass} className="text-lg" />}
        <p className="text-base font-bold text-center">
          {timeInfo.countdown || '---'}
        </p>
      </div>
    </div>
  );
};

function getTimeInfo(closeTime: string, openTime: string): TimeInfo {
  const now = new Date();
  const nowMs = now.getTime();

  // Convert "now" to Madrid wall-clock time (independent of browser timezone)
  const currentMinutes = getMadridMinutesSinceMidnight(now);

  // Parse market times
  const [closeHour, closeMin] = closeTime.split(':').map(Number);
  const [openHour, openMin] = openTime.split(':').map(Number);

  const marketCloseMinutes = closeHour * 60 + closeMin;
  const marketOpenMinutes = openHour * 60 + openMin;
  const graceEndMinutes = marketCloseMinutes + MARKET_GRACE_PERIOD_MINUTES;

  // Check if we're in the grace period (market just closed)
  const isInGracePeriod = currentMinutes >= marketCloseMinutes && currentMinutes < graceEndMinutes;

  if (isInGracePeriod) {
    return {
      label: 'Mercado cerrado',
      countdown: null,
      color: 'gray',
      showTimer: false,
    };
  }

  // Check if market is closed (after grace period until market opens)
  const isMarketClosed =
    currentMinutes >= graceEndMinutes ||
    currentMinutes < marketOpenMinutes;

  if (isMarketClosed) {
    // If we are already past the grace period, the opening is on the next Madrid day
    const addDays = currentMinutes >= graceEndMinutes ? 1 : 0;

    const marketOpenMs = madridWallClockToEpochMs(
      now,
      openHour,
      openMin,
      addDays
    );

    const timeUntilOpen = Math.max(0, marketOpenMs - nowMs);
    const countdown = formatCountdown(timeUntilOpen);

    return {
      label: 'Tiempo para la reapertura del mercado',
      countdown,
      color: 'red',
      showTimer: true,
    };
  }

  // Market open: time remaining until market closes today in Madrid
  const marketCloseMs = madridWallClockToEpochMs(
    now,
    closeHour,
    closeMin,
    0
  );

  const timeUntilClose = Math.max(0, marketCloseMs - nowMs);
  const countdown = formatCountdown(timeUntilClose);

  // Determine color based on time remaining
  const hoursRemaining = timeUntilClose / (1000 * 60 * 60);
  let color: 'red' | 'orange' | 'yellow' | 'green' = 'green';

  if (hoursRemaining < 1) color = 'red';
  else if (hoursRemaining < 3) color = 'orange';
  else if (hoursRemaining < 5) color = 'yellow';

  return {
    label: 'Tiempo para el cierre de mercado',
    countdown,
    color,
    showTimer: true,
  };
}

